"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const steps = [
  {
    number: "01",
    icon: "ClipboardDocumentCheckIcon",
    title: "Apply for a partner account",
    desc: "Fill in your business name, type, and contact details. Our team reviews all applications within 1 business day to ensure quality for pet owners.",
    color: "#5EBFB3",
    bg: "#E8F7F6",
    time: "5 minutes",
  },
  {
    number: "02",
    icon: "Cog6ToothIcon",
    title: "Set up your business profile",
    desc: "Add your services, operating hours, location, and team members. Configure which pet profile fields you need to see for each service type.",
    color: "#7C3AED",
    bg: "#EDE9FE",
    time: "1 afternoon",
  },
  {
    number: "03",
    icon: "RocketLaunchIcon",
    title: "Go live and grow",
    desc: "Your business appears on the DogFob partner map. Existing clients get a notification. New clients in your area can find and book you directly.",
    color: "#0369A1",
    bg: "#E0F2FE",
    time: "Instant",
  },
];

export default function BusinessOnboarding() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target
              .querySelectorAll(".scroll-fade-up, .scroll-scale")
              .forEach((el, i) => {
                setTimeout(() => el.classList.add("is-visible"), i * 110);
              });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 dark:bg-[#0f172a]"
      style={{ background: "linear-gradient(160deg, #F0FAFB 0%, #F7F8F6 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16 scroll-fade-up">
          <span className="teal-badge mb-4">Onboarding</span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight mt-4 mb-5">
            Up and running in{" "}
            <span className="gradient-text">one afternoon.</span>
          </h2>
          <p className="text-[#4A6363] dark:text-slate-400 text-lg max-w-lg mx-auto leading-relaxed">
            No developers needed. No complex integrations. DogFob is built to be set up by the business owner, not an IT department.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-10 relative">
          {/* Connector */}
          <div
            className="hidden md:block absolute top-[52px] left-[calc(16.67%+28px)] right-[calc(16.67%+28px)] h-[2px] z-0"
            style={{
              background: "linear-gradient(to right, #5EBFB3, #7C3AED, #0369A1)",
            }}
          />

          {steps.map((step, idx) => (
            <div key={step.number} className={`scroll-scale stagger-${idx + 1} relative z-10`}>
              <div className="feature-card p-7 h-full flex flex-col">
                {/* Icon */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 relative"
                    style={{ background: step.bg }}
                  >
                    <Icon
                      name={step.icon as "ClipboardDocumentCheckIcon"}
                      size={24}
                      style={{ color: step.color }}
                    />
                    <span
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full text-white text-[10px] font-extrabold font-heading flex items-center justify-center"
                      style={{ background: step.color }}
                    >
                      {idx + 1}
                    </span>
                  </div>
                  <p
                    className="text-xs font-bold uppercase tracking-widest"
                    style={{ color: step.color }}
                  >
                    Step {step.number}
                  </p>
                </div>

                <h3 className="text-xl font-bold text-[#1A2B2B] dark:text-slate-100 mb-3 font-heading leading-snug">
                  {step.title}
                </h3>
                <p className="text-[#4A6363] dark:text-slate-400 leading-relaxed text-sm flex-1">{step.desc}</p>

                <div
                  className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full w-fit"
                  style={{ background: step.bg, color: step.color }}
                >
                  <Icon name="ClockIcon" size={12} />
                  {step.time}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Partner types grid */}
        <div className="mt-16 scroll-fade-up">
          <p className="text-center text-sm font-semibold text-[#8AA4A4] dark:text-slate-500 uppercase tracking-widest mb-6">
            Open to all pet professionals
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { emoji: "🏥", label: "Veterinary Clinics" },
              { emoji: "🎓", label: "DogFob Trainers" },
              { emoji: "✂️", label: "Groomers" },
              { emoji: "🛒", label: "Pet Stores" },
              { emoji: "🦮", label: "DogFob Walkers" },
              { emoji: "🏠", label: "Boarding Facilities" },
            ].map((type) => (
              <div
                key={type.label}
                className="feature-card p-4 flex flex-col items-center gap-2 text-center"
              >
                <span className="text-2xl">{type.emoji}</span>
                <p className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 leading-tight">{type.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}